/**
 * "Vind underveis" route forecast (spec 11) — sampled lazily (button-driven,
 * never an automatic fan-out) at every 3rd waypoint, deduped by the same
 * 0.05° cell rounding the API proxy uses, so a long route with many
 * waypoints in the same forecast cell only costs one upstream fetch.
 */
import type { RoutePlan } from '@boatnav/core';
import { circularMeanDeg, windCompassLabel } from '@boatnav/core';
import type { WeatherResponse } from '@boatnav/schemas';
import type { Waypoint } from '../../lib/db.js';

export interface RouteWeatherSample {
  index: number;
  lat: number;
  lon: number;
  etaMs: number;
  windMs: number | null;
  windGustMs: number | null;
  windFromDeg: number | null;
}

export interface RouteWeatherSummary {
  samples: RouteWeatherSample[];
  minWindMs: number | null;
  maxWindMs: number | null;
  dominantDeg: number | null;
  dominantLabel: string | null;
}

const CELL_STEP = 0.05;

function roundCell(v: number): number {
  return Math.round(v / CELL_STEP) * CELL_STEP;
}

function cellKey(lat: number, lon: number): string {
  return `${roundCell(lat).toFixed(2)},${roundCell(lon).toFixed(2)}`;
}

/** Waypoint indices to sample: the start, then every 3rd thereafter. */
export function sampleIndices(waypointCount: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < waypointCount; i += 3) out.push(i);
  return out;
}

/** Cumulative ETA (epoch ms) at every waypoint, from the planned legs. */
export function waypointEtas(plan: RoutePlan, departureMs: number): number[] {
  const etas = [departureMs];
  let acc = departureMs;
  for (const leg of plan.legs) {
    acc += leg.durationMin * 60_000;
    etas.push(acc);
  }
  return etas;
}

function nearestHour(
  hours: WeatherResponse['hours'],
  atMs: number,
): WeatherResponse['hours'][number] | null {
  if (hours.length === 0) return null;
  return hours.reduce((best, cur) => (Math.abs(cur.ts - atMs) < Math.abs(best.ts - atMs) ? cur : best));
}

export type WeatherFetcher = (lat: number, lon: number) => Promise<WeatherResponse | null>;

export async function fetchForCoords(lat: number, lon: number): Promise<WeatherResponse | null> {
  try {
    const res = await fetch(`/api/weather?lat=${lat}&lon=${lon}`);
    if (!res.ok) return null;
    return (await res.json()) as WeatherResponse;
  } catch {
    return null;
  }
}

/**
 * Fetch + summarize wind along a planned route. `fetchImpl` is injectable so
 * tests can spy on call count instead of hitting the network (default is the
 * real `/api/weather` fetch).
 */
export async function computeRouteWeather(
  waypoints: Waypoint[],
  plan: RoutePlan,
  departureMs: number,
  fetchImpl: WeatherFetcher = fetchForCoords,
): Promise<RouteWeatherSummary> {
  const indices = sampleIndices(waypoints.length);
  const etas = waypointEtas(plan, departureMs);

  const cellFetches = new Map<string, Promise<WeatherResponse | null>>();
  const samples: RouteWeatherSample[] = [];

  for (const i of indices) {
    const wp = waypoints[i];
    if (!wp) continue;
    const key = cellKey(wp.lat, wp.lon);
    let pending = cellFetches.get(key);
    if (!pending) {
      pending = fetchImpl(wp.lat, wp.lon);
      cellFetches.set(key, pending);
    }
    const weather = await pending;
    const etaMs = etas[i] ?? departureMs;
    const hour = weather ? nearestHour(weather.hours, etaMs) : null;
    samples.push({
      index: i,
      lat: wp.lat,
      lon: wp.lon,
      etaMs,
      windMs: hour?.windMs ?? null,
      windGustMs: hour?.windGustMs ?? null,
      windFromDeg: hour?.windFromDeg ?? null,
    });
  }

  const withWind = samples.filter(
    (s): s is RouteWeatherSample & { windMs: number; windFromDeg: number } =>
      s.windMs != null && s.windFromDeg != null,
  );
  if (withWind.length === 0) {
    return { samples, minWindMs: null, maxWindMs: null, dominantDeg: null, dominantLabel: null };
  }

  const winds = withWind.map((s) => s.windMs);
  const dominantDeg = circularMeanDeg(withWind.map((s) => s.windFromDeg));

  return {
    samples,
    minWindMs: Math.min(...winds),
    maxWindMs: Math.max(...winds),
    dominantDeg,
    dominantLabel: windCompassLabel(dominantDeg),
  };
}
